import type { CentroArvoreNo, CentroNoResponse, CentroUsoResponse } from '@triade/contracts';

/**
 * Regras de exibicao da arvore de centros. Nenhum valor e calculado aqui: minutos, caminhos e contagens vem da API; o que existe
 * neste arquivo e ordem, visibilidade e quais destinos o formulario oferece (a API continua sendo quem recusa).
 */

const ORDEM = new Intl.Collator('pt-BR', { numeric: true, sensitivity: 'base' });

/** Ordem natural em cada nivel pelo identificador: "1" < "2" < "10" (recursivo). */
export function ordenarNatural(nos: CentroArvoreNo[]): CentroArvoreNo[] {
  return [...nos].sort((a, b) => ORDEM.compare(a.identificador, b.identificador)).map((n) => ({ ...n, filhos: ordenarNatural(n.filhos) }));
}

export interface LinhaArvore {
  no: CentroArvoreNo;
  nivel: number;
  temFilhos: boolean;
  aberto: boolean;
}

/** Linhas que aparecem, na ordem da arvore: os filhos de um no recolhido ficam fora. */
export function linhasVisiveis(nos: CentroArvoreNo[], recolhidos: ReadonlySet<string>): LinhaArvore[] {
  const out: LinhaArvore[] = [];
  const visita = (lista: CentroArvoreNo[]) => {
    for (const no of lista) {
      const temFilhos = no.filhos.length > 0;
      const aberto = temFilhos && !recolhidos.has(no.id);
      out.push({ no, nivel: no.nivel, temFilhos, aberto });
      if (aberto) visita(no.filhos);
    }
  };
  visita(ordenarNatural(nos));
  return out;
}

/** Ids de todos os nos que tem filhos (para "Recolher tudo"). */
export function idsComFilhos(nos: CentroArvoreNo[]): string[] {
  return todosNos(nos)
    .filter((n) => n.filhos.length > 0)
    .map((n) => n.id);
}

export function todosNos(nos: CentroArvoreNo[]): CentroArvoreNo[] {
  return nos.flatMap((n) => [n, ...todosNos(n.filhos)]);
}

export function encontrarNo(nos: CentroArvoreNo[], id: string): CentroArvoreNo | undefined {
  return todosNos(nos).find((n) => n.id === id);
}

/** O proprio no e todos os seus descendentes (ids). */
export function subarvoreIds(no: CentroArvoreNo): Set<string> {
  return new Set(todosNos([no]).map((n) => n.id));
}

export interface AlvoMover {
  /** null = raiz (sem pai). */
  id: string | null;
  rotulo: string;
}

/**
 * Destinos de "Mover para…": a raiz (se o centro tem pai) e os sinteticos ATIVOS que nao sao o proprio centro, nem um descendente
 * dele, nem o pai atual. Analitico nunca e destino (so sintetico tem filhos). A API valida de novo (MOVER_PARA_DESCENDENTE etc.).
 */
export function alvosDeMover(centro: Pick<CentroNoResponse, 'id' | 'parentId'>, nos: CentroArvoreNo[]): AlvoMover[] {
  const atual = encontrarNo(nos, centro.id);
  const bloqueados = atual ? subarvoreIds(atual) : new Set([centro.id]);
  const sinteticos = todosNos(nos)
    .filter((n) => n.tipoNo === 'SINTETICO' && n.ativo && !bloqueados.has(n.id) && n.id !== centro.parentId)
    .sort((a, b) => ORDEM.compare(a.caminho, b.caminho))
    .map((n): AlvoMover => ({ id: n.id, rotulo: rotuloCentro(n) }));
  return centro.parentId === null ? sinteticos : [{ id: null, rotulo: 'Raiz (sem pai)' }, ...sinteticos];
}

/** "3.1 · Prensagem": caminho e nome, como o contrato descreve para combos. */
export function rotuloCentro(no: Pick<CentroNoResponse, 'caminho' | 'nome'>): string {
  return `${no.caminho} · ${no.nome}`;
}

export interface RestricoesTipo {
  /** Motivo que impede virar sintetico (null = pode). */
  paraSintetico: string | null;
  /** Motivo que impede virar analitico (null = pode). */
  paraAnalitico: string | null;
}

/**
 * O campo Tipo so muda quando a API deixa. Sintetico com filhos nao vira analitico; analitico com CPM, etapa de ficha, lote ou
 * origem importada nao vira sintetico. Centro novo (sem `no`) pode ser qualquer um.
 */
export function restricoesTipo(no: CentroNoResponse | undefined, uso: CentroUsoResponse | undefined, filhos: number): RestricoesTipo {
  if (!no) return { paraSintetico: null, paraAnalitico: null };
  if (no.tipoNo === 'SINTETICO') {
    const n = Math.max(filhos, uso?.filhos ?? 0);
    return { paraSintetico: null, paraAnalitico: n > 0 ? `Tem ${n} ${n === 1 ? 'filho' : 'filhos'}: mova ou exclua os filhos antes` : null };
  }
  const usado = uso ? uso.cpm + uso.etapasFicha + uso.lotes + uso.importacao : 0;
  return {
    paraSintetico: usado > 0 ? 'Já tem CPM, etapa de ficha, lote ou origem importada: só analítico sem uso vira sintético' : null,
    paraAnalitico: null,
  };
}

/** "+ filho" so existe em sintetico (e o pai precisa estar ativo: a API recusa filho em pai inativo). */
export function podeCriarFilho(no: Pick<CentroNoResponse, 'tipoNo' | 'ativo'>): boolean {
  return no.tipoNo === 'SINTETICO' && no.ativo;
}
